import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './FeaturedHeroCarousel.css';
import { formatChannelEventDate } from '../utils/channelDateUtils';
import { useMediaState } from '../hooks/useMediaState';
import WatchButton from './common/WatchButton';
import { FALLBACK_IMAGE_SRC } from '../utils/ImageUtils';
import { imageUrl } from '../api/image';

const HeroSlideEventDate = ({ channel }) => {
  // The server's freshest scheduled start (`starts_at`, from /v1/live once it is in the live set).
  const media = useMediaState(channel);
  const formatted = formatChannelEventDate(media.starts_at);
  if (!formatted) {
    return null;
  }
  return <p className="mb-4 text-lg text-white/80 font-display tracking-wide">{formatted}</p>;
};

/** The home hero: the `/v1/home` hero rail (else the first rail), one slide per card. */
const FeaturedHeroCarousel = ({ category }) => {
  const navigate = useNavigate();
  const channels = category?.channels || [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const sliderRef = useRef(null);

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 8000,
    arrows: true,
    beforeChange: (current, next) => setCurrentIndex(next),
  };

  // A media card plays; an event or fighter card opens its page; an article its bkfc.com page.
  const openCard = channel => {
    if (channel.link) {
      navigate(channel.link);
    } else if (channel.href) {
      window.open(channel.href, '_blank', 'noopener');
    } else {
      navigate(`/watch/${channel.slug || channel._id}`);
    }
  };

  if (!channels.length) {
    return (
      <div
        className="featured-hero-carousel relative mb-8 bg-primary flex items-center justify-center"
        ref={carouselRef}
      >
        <div className="text-center text-white/50">No featured content available</div>
      </div>
    );
  }

  return (
    <div className="featured-hero-carousel relative" ref={carouselRef}>
      {channels.length > 0 ? (
        <Slider {...sliderSettings} ref={sliderRef}>
          {channels.map((channel, index) => {
            const slideBackgroundImage =
              imageUrl(channel.art?.landscape, '16x9', 1920) || FALLBACK_IMAGE_SRC;
            const slideTitle = channel.title || 'Untitled';

            return (
              <div key={`${channel._id || index}`} className="relative h-[100vh]">
                {/* Background Image with Gradient Overlay */}
                <div
                  className="absolute inset-0 bg-center bg-cover"
                  style={{ backgroundImage: `url(${slideBackgroundImage})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to top, #141413 0%, rgba(20,20,19,0.92) 30%, rgba(20,20,19,0.7) 55%, rgba(20,20,19,0.3) 80%, transparent 100%)',
                    }}
                  />
                </div>

                {/* Overlay Content - Left-aligned */}
                <div className="absolute inset-0 flex flex-col justify-center items-start container mx-auto px-6 z-10">
                  <h1 className="mb-2 text-4xl font-bold text-white md:text-6xl font-display">
                    {slideTitle}
                  </h1>

                  <HeroSlideEventDate channel={channel} />

                  {/* Inline Carousel Dots (below title/date metadata) */}
                  {channels.length > 1 && (
                    <div
                      className="featured-inline-dots mb-5"
                      role="tablist"
                      aria-label="Featured slides"
                    >
                      {channels.map((_, dotIndex) => (
                        <button
                          key={`hero-dot-${dotIndex}`}
                          type="button"
                          className={`featured-inline-dot ${
                            dotIndex === currentIndex ? 'is-active' : ''
                          }`}
                          onClick={() => {
                            setCurrentIndex(dotIndex);
                            sliderRef.current?.slickGoTo(dotIndex);
                          }}
                          aria-label={`Go to slide ${dotIndex + 1}`}
                          aria-selected={dotIndex === currentIndex}
                        />
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4 mb-6">
                    {channel.entityType === 'media' || !channel.link ? (
                      <WatchButton
                        item={channel}
                        className="featured-hero-cta text-lg"
                        onPlay={() => {
                          setCurrentIndex(index);
                          openCard(channel);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="featured-hero-cta flex items-center px-6 py-3 font-bold text-white bg-white/20 hover:bg-white/30 border border-white/30 font-display text-lg uppercase tracking-wide"
                        onClick={() => {
                          setCurrentIndex(index);
                          openCard(channel);
                        }}
                      >
                        Learn More
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      ) : null}
    </div>
  );
};

export default FeaturedHeroCarousel;
